import React, { useMemo, useState } from 'react';
import {
  ArrowRight,
  Bot,
  Check,
  ChevronDown,
  CreditCard,
  ShieldCheck,
  Sparkles,
  Workflow,
  Zap
} from 'lucide-react';

const plans = [
  {
    id: 'free',
    name: 'Free',
    badge: 'Start here',
    price: 'R0',
    period: '/month',
    description: 'Explore ParrotSheets, build simple workspaces, and get a feel for structured sheet-based applications.',
    cta: 'Get started free',
    secondary: true,
    features: [
      'Up to 2 workspaces',
      'Basic sheets and manual entry',
      'Basic workspace UI presets',
      'Limited sharing',
      'Limited AI actions',
      'No advanced automations or agents'
    ]
  },
  {
    id: 'starter',
    name: 'Starter',
    badge: 'For individuals',
    price: 'R180',
    period: '/month',
    description: 'Affordable power for solo operators, teachers, creators, and small business users building real workflows.',
    cta: 'Choose Starter',
    features: [
      'More workspaces and sheets',
      'Basic AI usage',
      'Report exports',
      'Collaboration features',
      '200 AI and automation actions / month'
    ]
  },
  {
    id: 'pro',
    name: 'Pro',
    badge: 'Most popular',
    price: 'R450',
    period: '/month',
    description: 'Full ParrotSheets power for serious operations, advanced outputs, and automation-driven work.',
    cta: 'Choose Pro',
    highlight: true,
    features: [
      'Everything in Starter',
      'Full AI capabilities',
      'Agent workflows',
      'Automations and triggers',
      'Advanced outputs: reports, slides, dashboards',
      'Cross-sheet intelligence',
      '1000 AI and automation actions / month'
    ]
  },
  {
    id: 'business',
    name: 'Business',
    badge: 'For teams',
    price: 'From R999',
    period: '/month',
    description: 'Shared environments, stronger controls, and team-level intelligence for organisations and operational teams.',
    cta: 'Talk to Sales',
    features: [
      'Multi-user workspaces',
      'Roles and permissions',
      'Shared team environments',
      'Advanced workspace controls',
      'Team-level AI and agent flows',
      'Priority support'
    ]
  }
];

const faqs = [
  {
    q: 'Is there a free plan?',
    a: 'Yes. You can start with Free and use ParrotSheets for lighter work without paying upfront.'
  },
  {
    q: 'Do I need to start with a trial?',
    a: 'No. You can start directly on the Free plan, or choose a paid plan if you already know what you need.'
  },
  {
    q: 'How does the reverse trial work?',
    a: 'New users may be given temporary Pro access so they can experience the product at full power. When that access ends, they can move to Free, Starter, Pro, or Business.'
  },
  {
    q: 'Will I lose work created during a trial?',
    a: 'No. Your sheets, rows, and workspaces remain intact. Only premium capabilities such as advanced AI, agents, and automations become limited if you move down to a lower tier.'
  }
];

const recapCards = [
  {
    icon: Bot,
    title: 'AI-powered insights',
    text: 'Analyse rows and sheets together, then generate summaries, reports, and operational decisions faster.'
  },
  {
    icon: Workflow,
    title: 'Agent workflows',
    text: 'Automate repetitive steps and keep work moving through guided actions and logic.'
  },
  {
    icon: Sparkles,
    title: 'Smart outputs',
    text: 'Turn workspace data into reports, slides, and visual outputs without rebuilding the information elsewhere.'
  },
  {
    icon: Zap,
    title: 'App-like workspaces',
    text: 'Your sheets behave like structured applications with UI, workflows, and shared operational context.'
  }
];

function SectionLabel({ children }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-teal-700 shadow-sm">
      <Sparkles className="h-3.5 w-3.5" />
      {children}
    </div>
  );
}

function PlanCard({ plan }) {
  return (
    <div
      className={`relative flex h-full w-full max-w-5xl flex-col rounded-[28px] border bg-white p-6 shadow-sm transition-all ${
        plan.highlight
          ? 'border-teal-500 shadow-[0_30px_70px_-30px_rgba(0,128,128,0.45)] hover:-translate-y-1'
          : 'border-slate-200 hover:-translate-y-1'
      }`}
    >
      {plan.highlight ? (
        <div className="absolute -top-3 left-6 rounded-full bg-teal-600 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-white">
          Best value for serious use
        </div>
      ) : null}

      <div className="flex items-start justify-between gap-3">
        <div>
          <div
            className={`inline-flex rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] ${
              plan.highlight ? 'bg-teal-50 text-teal-700' : 'bg-slate-100 text-slate-600'
            }`}
          >
            {plan.badge}
          </div>
          <h3 className="mt-4 text-2xl font-bold tracking-tight text-slate-900">{plan.name}</h3>
          <p className="mt-2 text-sm leading-6 text-slate-500">{plan.description}</p>
        </div>
      </div>

      <div className="mt-6 flex items-end gap-1">
        <span className="text-4xl font-bold tracking-tight text-slate-900">{plan.price}</span>
        <span className="pb-1 text-sm text-slate-500">{plan.period}</span>
      </div>

      <ul className="mt-6 space-y-3 text-sm text-slate-700">
        {plan.features.map((feature) => (
          <li key={feature} className="flex items-start gap-3">
            <span
              className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                plan.highlight ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-600'
              }`}
            >
              <Check className="h-3.5 w-3.5" />
            </span>
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <div className="mt-8 pt-2">
        <button
          type="button"
          className={`inline-flex w-full items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold transition ${
            plan.highlight
              ? 'bg-teal-600 text-white hover:bg-teal-700'
              : plan.secondary
                ? 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                : 'bg-slate-900 text-white hover:bg-slate-700'
          }`}
        >
          {plan.cta}
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function FAQItem({ item, isOpen, onToggle }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-4 text-left"
      >
        <span className="text-sm font-semibold text-slate-900">{item.q}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-500 transition ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <div
        className={`grid overflow-hidden transition-[grid-template-rows,opacity] duration-200 ${
          isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <p className="pt-3 text-sm leading-6 text-slate-600">{item.a}</p>
        </div>
      </div>
    </div>
  );
}

export default function ParrotSheetsPricingPage() {
  const [annual, setAnnual] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const trialHighlights = useMemo(() => [
    'Temporary access to Pro features',
    'Experience AI, agents, and automations',
    'Move to Free, Starter, Pro, or Business afterwards'
  ], []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f4fbfb] via-white to-slate-50 text-slate-900">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
        <section className="overflow-hidden rounded-[32px] border border-teal-100 bg-white shadow-[0_40px_110px_-48px_rgba(15,23,42,0.35)]">
          <div className="grid gap-10 px-6 py-8 lg:grid-cols-[minmax(0,1.2fr)_360px] lg:px-10 lg:py-10">
            <div>
              <SectionLabel>ParrotSheets pricing</SectionLabel>
              <h1 className="mt-5 max-w-3xl text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
                Choose the plan that fits how you want to build and run workspaces
              </h1>
              <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg">
                From simple sheet-based workspaces to AI-powered operational systems, ParrotSheets gives you room to start free, scale gradually, and unlock more intelligence when you need it.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-2xl bg-teal-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-teal-700"
                >
                  Get started free
                  <ArrowRight className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  Start free trial
                </button>
              </div>
            </div>

            <div className="rounded-[28px] border border-slate-200 bg-slate-50 p-5">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <ShieldCheck className="h-4 w-4 text-teal-600" />
                Why teams choose ParrotSheets
              </div>
              <div className="mt-5 grid gap-3">
                {[
                  { label: 'Structured workspaces', value: 'Apps from sheets' },
                  { label: 'AI layer', value: 'Reports and insights' },
                  { label: 'Agent layer', value: 'Actions and automation' }
                ].map((metric) => (
                  <div key={metric.label} className="rounded-2xl border border-white bg-white p-4 shadow-sm">
                    <div className="text-base font-bold text-slate-900">{metric.value}</div>
                    <div className="mt-1 text-sm text-slate-500">{metric.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="mt-12">
          <div className="max-w-2xl">
            <SectionLabel>What you are paying for</SectionLabel>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900">More than spreadsheets</h2>
            <p className="mt-3 text-base leading-7 text-slate-600">
              ParrotSheets helps you build structured workspaces where sheets can render as interfaces, support workflows, connect across workspaces, and generate meaningful outputs.
            </p>
          </div>

          <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {recapCards.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="rounded-[26px] border border-slate-200 bg-white p-5 shadow-sm transition-transform hover:-translate-y-1"
                >
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-50 text-teal-700">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-slate-900">{item.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{item.text}</p>
                </div>
              );
            })}
          </div>
        </section>

        <section className="mt-12">
          <div className="flex flex-col gap-4 rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-slate-900">Simple plans that scale with your use</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Start with Free, move into Starter for practical use, or unlock Pro and Business for deeper automation and intelligence.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-1">
              <button
                type="button"
                onClick={() => setAnnual(false)}
                className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${!annual ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setAnnual(true)}
                className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${annual ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
              >
                Annual
              </button>
            </div>
          </div>

          <div className="mt-8 grid gap-6 xl:grid-cols-4">
            {plans.map((plan) => {
              const adjustedPlan = annual && plan.id !== 'free'
                ? {
                    ...plan,
                    price:
                      plan.id === 'starter'
                        ? 'R790'
                        : plan.id === 'pro'
                          ? 'R2 490'
                          : 'From R9 990',
                    period: '/year'
                  }
                : plan;
              return <PlanCard key={plan.id} plan={adjustedPlan} />;
            })}
          </div>
        </section>

        <section className="mt-12 grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_420px]">
          <div className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm">
            <SectionLabel>Usage and add-ons</SectionLabel>
            <h2 className="mt-4 text-2xl font-bold tracking-tight text-slate-900">AI and automation usage</h2>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-600">
              Paid plans include monthly AI and automation actions. When you need more, you can add credits without changing your plan.
            </p>
            <div className="mt-6 grid gap-4 sm:grid-cols-3">
              {[
                { tier: 'Free', usage: 'Limited access', note: 'For exploration and light use' },
                { tier: 'Starter', usage: '200 actions', note: 'For steady individual work' },
                { tier: 'Pro', usage: '1000 actions', note: 'For active operational workflows' }
              ].map((item) => (
                <div key={item.tier} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-900">{item.tier}</p>
                  <p className="mt-2 text-2xl font-bold tracking-tight text-teal-700">{item.usage}</p>
                  <p className="mt-2 text-sm text-slate-500">{item.note}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-[30px] border border-slate-200 bg-gradient-to-br from-teal-50 via-white to-white p-6 shadow-sm">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-100 text-teal-700">
              <CreditCard className="h-5 w-5" />
            </div>
            <h2 className="mt-4 text-2xl font-bold tracking-tight text-slate-900">Need the full experience first?</h2>
            <p className="mt-3 text-sm leading-7 text-slate-600">
              You can start with a free trial to experience Pro-level capabilities before deciding which plan fits you best.
            </p>
            <div className="mt-6 space-y-3">
              {trialHighlights.map((item) => (
                <div key={item} className="flex items-start gap-3 rounded-2xl border border-white bg-white p-3 shadow-sm">
                  <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-teal-100 text-teal-700">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-sm text-slate-700">{item}</span>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-700"
            >
              Start free trial
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </section>

        <section className="mt-12 grid gap-6 lg:grid-cols-[minmax(0,1fr)_420px]">
          <div className="rounded-[30px] border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-white p-8 shadow-sm">
            <SectionLabel>After a trial</SectionLabel>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900">When a trial ends, your options stay open</h2>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-600">
              If you start with temporary Pro access, you are not locked into anything. You can move to Free, Starter, Pro, or Business based on how much power you want to keep.
            </p>
            <div className="mt-6 space-y-3">
              {[
                'Your sheets, rows, and workspaces remain preserved',
                'Advanced AI, agent, and automation features may become limited on lower tiers',
                'You can upgrade or downgrade as your needs change'
              ].map((item) => (
                <div key={item} className="flex items-start gap-3 rounded-2xl border border-white bg-white p-3 shadow-sm">
                  <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-sm text-slate-700">{item}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-2xl bg-teal-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-teal-700"
              >
                Start free trial
                <ArrowRight className="h-4 w-4" />
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Get started free
              </button>
            </div>
          </div>

          <div className="space-y-3">
            {faqs.map((item, index) => (
              <FAQItem
                key={item.q}
                item={item}
                isOpen={openFaq === index}
                onToggle={() => setOpenFaq(openFaq === index ? -1 : index)}
              />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
